/**
 * customerAddress.js — job-site address resolution helper.
 *
 * PR-73 Commit 3 (dispatches 427/433). Consumers: JobDetail.js
 * (replaces the inline chain at :742), WorkOrderDetail.js (replaces
 * the wo.address || wo.location slot per dispatch 433 §3).
 *
 * PR-73 Commit 4 (dispatches 436/437/438). Adds:
 *   - formatJobSiteAddress + resolveWorkOrderJobSiteString for the
 *     WorkOrderDetail slot (behavioural test coverage of the wire
 *     that Commit 3's source-regex assertion could not provide).
 *   - Step 3 of the precedence chain: job.{address1,address2,city,
 *     state,postal} synthesis, evidenced by tenant-270 live read.
 *
 * ── The concept and where it lives ──────────────────────────────────
 *
 * A JOB's rendered address today is the JOB'S address, not the
 * customer's home (JobDetail:739-741, PR-72 comment). Reading C from
 * dispatch 431 §3 named the customer-level designation
 * (customer.addresses[i].is_job_site) that expresses which of a
 * customer's addresses is the site of work.
 *
 * This module resolves that concept from whatever shape the record
 * actually carries. It is intentionally NOT in canonicalFields.js
 * (dispatch 433 §2): makeAccessor returns a single value from an
 * alias chain and warns on disagreement. This helper returns an
 * OBJECT from a collection scan with defensive fallbacks — different
 * shape. Consolidating them would produce the wrong abstraction.
 *
 * ── Precedence chain (dispatches 433 §2, 437 §6, 438 §1) ──────────
 *
 *   1. job.address           — job-level snapshot object.
 *                              SPECULATIVE as of 2026-08-24 (dispatch 437
 *                              §4): zero writers found in FE/BE/migration,
 *                              and zero occurrences across 500 live
 *                              tenant-270 job records (438 §1). Retained
 *                              because an object-shaped job address is
 *                              the shape a future writer would most
 *                              plausibly use; branch is inert today.
 *                              Re-evaluate before the next writer census.
 *   2. job.job_address       — job-level snapshot object, legacy alt.
 *                              SPECULATIVE as step 1, same date, same
 *                              measurement.
 *   3. job.{address1,address2,city,state,postal} synthesised
 *                              EVIDENCED: writer is migrateJobs():1160
 *                              (bidit/migration/cmd/migrate/entities.go),
 *                              which calls putIf() unconditionally on
 *                              non-blank Quote_Address1/2/City/State/Postal.
 *                              Live-measured (438 §1) at 500/500 non-blank
 *                              address1 on the first page of tenant-270
 *                              jobs, 479 distinct values, has_more=true so
 *                              the bound is "first 500", not the population.
 *                              Guarded on address1 presence (437 §4.1) so a
 *                              stray city/state on some other record does
 *                              not synthesise an empty-line1 object.
 *   4. customer.addresses[i] where is_job_site: true
 *                              (Reading C designation, PR-73 Commit 2)
 *   5. customer.addresses[i] where is_primary: true
 *   6. customer.addresses[0]
 *   7. customer.address                  (singular legacy shape)
 *   8. customer.serviceAddress || customer.service_address
 *                              BOTH spellings live (dispatch 439 §1.1).
 *                              Camel: migration writer, entities.go:61,178.
 *                              Snake: app write path + quote-builder;
 *                              7 dev-table customer records measured with
 *                              snake by unnest-address-objects.py.
 *                              cmd/customers/main.go:317 also synthesises
 *                              camel from flat address1/city/state/postal
 *                              at customer-read time.
 *   9. customer.address_street/city/state/zip
 *                              (5th spelling found at JobDetail:742
 *                              during Commit 3 recon; added to PORTICA-89)
 *  10. null
 *
 * Ordering rationale (dispatch 437 §5): a job-level site address is a
 * historical fact about where work happened. The customer's is_job_site
 * designation is a live customer-level default. Facts outrank defaults —
 * a completed job must not silently re-address itself when someone later
 * edits the customer.
 *
 * The chain terminates at null; consumers decide how to render an
 * absent address (JobDetail lets GoldenCustomerCard show its own
 * empty state; WorkOrderDetail renders 'No address').
 *
 * ── Return shape ─────────────────────────────────────────────────────
 *
 *   { line1, city, state, zip } — normalised.
 *
 * Alias normalisation at the boundary: street → line1, postal → zip,
 * address1 → line1, address_street → line1, address_zip → zip, etc.
 * Consumers do NOT reconcile shapes themselves.
 *
 * ── Defensive contract (dispatch 429 §4) ─────────────────────────────
 *
 * The client-side writer (AddCustomerDrawer, CustomerFormPanel) enforces
 * exactly-one is_job_site. The backend passthrough enforces nothing
 * (dispatch 428 §1). Every reader must be defensive:
 *
 *   0 is_job_site entries → step 5 (is_primary) → step 6 (index 0).
 *                           No warning — legitimate flag-checked state.
 *   1 is_job_site entry   → return it (step 4). No warning.
 *   ≥2 is_job_site entries → return the FIRST (deterministic), and
 *                            LOG A WARNING. The record violates a
 *                            writer invariant; consumers should hear
 *                            about it. Mirrors canonicalFields N-14
 *                            divergence log.
 *
 * The warning is on a module-private function that can be replaced
 * for tests via __setWarnForTests. Consumers see console.warn in
 * production.
 *
 * ── Reader-pairing note (dispatch 432 §3.2) ─────────────────────────
 *
 * This reader consults CUSTOMER-level data (customer.addresses[i]).
 * Step 3 now also reads JOB-level flat scalars — see PORTICA-25 and
 * dispatch 431 §3. When a proper snapshot writer lands (a future PR),
 * a paired reader will prefer job.job_site_address over the customer
 * lookup this helper performs. Both readers will need to coexist;
 * their pairing should be co-documented at that point.
 *
 * Today: this helper reads live customer data below step 3. A customer
 * edit propagates to every job referencing the customer at the next
 * render — UNLESS the job carries its own address1 (steps 1-3), in
 * which case the historical fact wins.
 */

// Warn hook — replaceable for tests. Never store the warnSpy in a
// module-global exposed to production code paths.
let _warn = (msg, ctx) => {
  // eslint-disable-next-line no-console
  console.warn(msg, ctx);
};

/** @internal test-only */
export function __setWarnForTests(fn) {
  _warn = fn || ((msg, ctx) => { /* eslint-disable-next-line no-console */ console.warn(msg, ctx); });
}

// Predicates that identify a non-empty address entry. Matches the
// PR-73 Commit 1 writer filter (line1 || zip) so writer + reader
// agree on what counts as "a real address".
function hasAddressContent(a) {
  if (!a || typeof a !== 'object') return false;
  return !!(a.line1 || a.zip || a.street || a.address1 || a.postal);
}

// Normalise any of the ~5 known address shapes to { line1, city, state, zip }.
function normalise(a) {
  if (!a || typeof a !== 'object') return null;
  return {
    line1: a.line1 || a.street || a.address1 || '',
    line2: a.line2 || a.address2 || '',
    city: a.city || '',
    state: a.state || '',
    zip: a.zip || a.postal || '',
  };
}

// IDI-247 (2026-09-09): Write-side converter for customer address payloads.
// The frontend form uses drawer-shape `{line1, line2, city, state, zip, ...}`;
// the backend read path derives `address` from the canonical stored shape
// `serviceAddress: {address1, address2, city, state, postal}` (see
// cmd/customers/main.go:373-381). Writing `address:` is silently overwritten
// by that derivation on next read; writing `addresses[]` is persistent but
// outranks `serviceAddress` in getJobSiteAddress by four steps (step 5 vs 8),
// which is a latent trap. The fix per the ticket: emit `serviceAddress` in
// the stored shape and stop emitting the two shadow keys.
//
// Handles the `address2` case that was silently dropped on 23 customers:
// drawer `line2` maps to backend `address2` — both directions are aliased
// by normalise() above, so the read side already handles either shape.
//
// Returns null if the drawer address is empty (no line1 or zip); callers
// should conditionally spread the result into the payload.
export function toServiceAddressShape(drawerAddress) {
  if (!drawerAddress || typeof drawerAddress !== 'object') return null;
  const line1 = drawerAddress.line1 || drawerAddress.address1 || drawerAddress.street || '';
  const zip = drawerAddress.zip || drawerAddress.postal || '';
  if (!line1 && !zip) return null;
  return {
    address1: line1,
    address2: drawerAddress.line2 || drawerAddress.address2 || '',
    city: drawerAddress.city || '',
    state: drawerAddress.state || '',
    postal: zip,
  };
}

// Synthesise from customer.address_street/city/state/zip (the fifth
// spelling flagged at JobDetail:742). Only if at least street is
// present — a customer with no addresses at all should return null,
// not a normalised empty object.
function fromAddressFieldsShape(customer) {
  if (!customer || !customer.address_street) return null;
  return {
    line1: customer.address_street,
    line2: '',
    city: customer.address_city || '',
    state: customer.address_state || '',
    zip: customer.address_zip || '',
  };
}

// Synthesise from job.{address1,address2,city,state,postal} — the flat
// five-scalar shape migrateJobs():1160 writes onto every migrated job
// record. Live-measured 500/500 non-blank address1 on tenant-270's
// first page (dispatch 438 §1); 479 distinct values.
//
// Guarded on address1 per dispatch 437 §4.1: city/state/postal are
// generic top-level keys and could exist on some future job without
// address1 — without this guard a stray city yields an empty-line1
// object that renders as a comma salad rather than falling through
// to the customer chain.
//
// Alias mapping (boundary normalisation, consumers reconcile nothing):
//   address2 → line2
//   postal   → zip
function fromJobFlatAddressShape(job) {
  if (!job || !job.address1) return null;
  return {
    line1: job.address1,
    line2: job.address2 || '',
    city: job.city || '',
    state: job.state || '',
    zip: job.postal || '',
  };
}

/**
 * Resolve the job-site address for a job/customer pair.
 *
 * @param {Object} args
 * @param {Object} [args.job]      — job record (may be null/undefined).
 * @param {Object} [args.customer] — customer record (may be null/undefined).
 * @returns {{line1:string, city:string, state:string, zip:string}|null}
 */
export function getJobSiteAddress(args = {}) {
  const { job = null, customer = null } = args;

  // 1 & 2: job-level snapshot object (backwards compat, speculative).
  if (hasAddressContent(job?.address)) return normalise(job.address);
  if (hasAddressContent(job?.job_address)) return normalise(job.job_address);

  // 3: job.{address1,address2,city,state,postal} — the migrated shape
  // (dispatch 438 §1). Guarded on address1 (437 §4.1).
  const fromJobFlat = fromJobFlatAddressShape(job);
  if (fromJobFlat) return fromJobFlat;

  // 4-6: customer.addresses[] chain, per Reading C.
  const list = Array.isArray(customer?.addresses) ? customer.addresses : [];
  const nonEmpty = list.filter(hasAddressContent);
  if (nonEmpty.length > 0) {
    const designated = nonEmpty.filter((a) => a.is_job_site === true);
    if (designated.length >= 2) {
      _warn(
        'getJobSiteAddress: multiple is_job_site entries; writer invariant violated. Returning the first.',
        { count: designated.length, customerId: customer?.id },
      );
      return normalise(designated[0]);
    }
    if (designated.length === 1) {
      return normalise(designated[0]);
    }
    // 0 designated: step 5 (is_primary) → step 6 (index 0).
    const primary = nonEmpty.find((a) => a.is_primary === true);
    if (primary) return normalise(primary);
    return normalise(nonEmpty[0]);
  }

  // 7: singular legacy customer.address.
  if (hasAddressContent(customer?.address)) return normalise(customer.address);

  // 8: serviceAddress legacy — BOTH spellings. Camel is what the
  // migration writes (entities.go:61,178 pack an object into
  // item["serviceAddress"]). Snake is what the app's own write path
  // produces and cmd/customers/main.go:441 passes through unchanged.
  //
  // Both are live. unnest-address-objects.py (migration/scripts,
  // dispatch 439 §1.1) measured 7 dev-table customer records
  // carrying snake-case `service_address` alongside `billing_address`.
  // Quote-builder writes snake (useQuoteAutoSave.js:288 whitelist);
  // quoteDisplayResolution.js:178 already does the both-spellings
  // coalesce for the same reason.
  //
  // Pre-Commit-4 this branch was reachable via the WorkOrderDetail:994
  // inline override. Commit 4 deleted that override; the coverage
  // must land here or snake becomes unreachable. The comment at
  // WorkOrderDetail:1002 claimed this was preserved; dispatch 439
  // §1 caught the lie — the ownership is here now, in truth as well
  // as in intent.
  // Coalesce hasAddressContent-aware so an empty camel shell does not
  // shadow a real snake. `customer.serviceAddress = {}` is more plausibly
  // a writer artifact (some migration passes have produced it) than an
  // intentional negative signal; the empty check gates whether it
  // short-circuits the OR. Mirrors step 1's guard on job?.address.
  const camel = hasAddressContent(customer?.serviceAddress) ? customer.serviceAddress : null;
  const snake = hasAddressContent(customer?.service_address) ? customer.service_address : null;
  if (camel) return normalise(camel);
  if (snake) return normalise(snake);

  // 9: address_street/city/state/zip fifth spelling.
  const fromFields = fromAddressFieldsShape(customer);
  if (fromFields) return fromFields;

  // 10: nothing.
  return null;
}

/**
 * formatJobSiteAddress — pure formatter for the { line1, city, state, zip }
 * shape returned by getJobSiteAddress. Extracted from the WorkOrderDetail
 * "Job site" slot IIFE (PR-73 Commit 4) so the slot's behaviour is
 * unit-testable without paying the WorkOrderDetail render cost — dispatch
 * 436 §6.21 (a source-regex assertion proves the characters were typed,
 * never that the wire works; the tell is that fixing the defect reddens
 * the test).
 *
 * Returns 'No address' for null/missing input and for shapes whose fields
 * are all empty, matching the pre-extraction slot behaviour.
 */
export function formatJobSiteAddress(addr) {
  if (!addr) return 'No address';
  const joined = [addr.line1, addr.city, addr.state, addr.zip]
    .filter(Boolean)
    .join(', ');
  return joined || 'No address';
}

/**
 * resolveWorkOrderJobSiteString — behavioural resolver for the
 * WorkOrderDetail "Job site" slot. Encapsulates BOTH the correct key
 * access on the WO state object AND the formatting.
 *
 * dispatch 436 §2 established that `wo.job` has no writer — the two
 * setWO hydrations attach the parent job under `parentJob`. Commit 3
 * shipped `getJobSiteAddress({ customer, job: wo?.job })` which read
 * an unwritten key on every render, making steps 1–2 of the helper's
 * precedence dead on this surface while alive on JobDetail. Same helper,
 * two precedences — the Family C shape C-03 was written to prevent,
 * reintroduced by the commit that claimed to close it.
 *
 * This resolver names the writer (`wo.parentJob`) and makes the wire a
 * property of behaviour rather than a property of source text. If the
 * key is mis-wired here, the test at customerAddress.test.js reddens.
 */
export function resolveWorkOrderJobSiteString({ wo, customer }) {
  const addr = getJobSiteAddress({ customer, job: wo?.parentJob });
  return formatJobSiteAddress(addr);
}
